/**
 * MyCardsSection — cross-board card list showing all cards assigned to the
 * current user, sorted by due date (soonest first, no-due-date last).
 * Supports cursor-based pagination with "Load more" button.
 *
 * Validates: Requirements 11.1, 11.2, 11.3, 11.5
 */

import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../../services/api-client';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MyCardLabel {
  readonly labelId: string;
  readonly name: string;
  readonly color: string;
}

interface MyCardItem {
  readonly cardId: string;
  readonly cardTitle: string;
  readonly boardId: string;
  readonly boardTitle: string;
  readonly listTitle: string;
  readonly dueDate: string | null;
  readonly labels: MyCardLabel[];
}

interface MyCardsResponse {
  readonly data: MyCardItem[];
  readonly pagination: {
    readonly nextCursor: string | null;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDueDate(isoDate: string | null): string {
  if (!isoDate) return 'No due date';
  const date = new Date(isoDate);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function isDueDateOverdue(isoDate: string | null): boolean {
  if (!isoDate) return false;
  return new Date(isoDate).getTime() < Date.now();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MyCardsSection(): React.ReactElement {
  const [cards, setCards] = useState<MyCardItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCards = useCallback(async (cursor?: string): Promise<void> => {
    const isInitial = !cursor;
    if (isInitial) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const query: Record<string, string> = { limit: '25' };
      if (cursor) {
        query.cursor = cursor;
      }
      const response = await api.get<MyCardsResponse>('/v1/users/me/cards', query);
      const newCards = response.data ?? [];
      const pagination = response.pagination ?? { nextCursor: null };

      if (isInitial) {
        setCards(newCards);
      } else {
        setCards((prev) => [...prev, ...newCards]);
      }
      setNextCursor(pagination.nextCursor);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load cards';
      setError(message);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const handleLoadMore = useCallback(() => {
    if (nextCursor) {
      fetchCards(nextCursor);
    }
  }, [nextCursor, fetchCards]);

  const handleCardClick = useCallback((boardId: string, cardId: string) => {
    window.location.href = `/boards/${boardId}/cards/${cardId}`;
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <section aria-label="My cards" className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">My Cards</h3>
        <div className="flex items-center gap-2 py-4" role="status" aria-label="Loading cards">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
          <span className="text-sm text-muted-foreground">Loading cards…</span>
        </div>
      </section>
    );
  }

  // Error state
  if (error && cards.length === 0) {
    return (
      <section aria-label="My cards" className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">My Cards</h3>
        <p className="text-sm text-destructive" role="alert">{error}</p>
        <Button size="sm" variant="outline" onClick={() => fetchCards()}>
          Retry
        </Button>
      </section>
    );
  }

  return (
    <section aria-label="My cards" className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">My Cards</h3>

      {cards.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No cards assigned to you.
        </p>
      ) : (
        <ul role="list" className="divide-y divide-border rounded-md border">
          {cards.map((card) => (
            <li key={`${card.boardId}-${card.cardId}`}>
              <button
                type="button"
                onClick={() => handleCardClick(card.boardId, card.cardId)}
                className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring transition-colors"
                aria-label={`Card: ${card.cardTitle} in ${card.boardTitle}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {card.cardTitle}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {card.boardTitle} • {card.listTitle}
                  </p>
                  {/* Labels */}
                  {card.labels.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {card.labels.map((label) => (
                        <span
                          key={label.labelId}
                          className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium text-white"
                          style={{ backgroundColor: label.color }}
                        >
                          {label.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Due date */}
                <Badge
                  className={
                    isDueDateOverdue(card.dueDate)
                      ? 'shrink-0 bg-destructive/10 text-destructive border-destructive/20'
                      : 'shrink-0 bg-muted text-muted-foreground'
                  }
                >
                  {formatDueDate(card.dueDate)}
                </Badge>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Load more */}
      {nextCursor && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleLoadMore}
          disabled={isLoadingMore}
          className="w-full"
        >
          {isLoadingMore ? 'Loading…' : 'Load more'}
        </Button>
      )}

      {/* Error loading more */}
      {error && cards.length > 0 && (
        <p className="text-xs text-destructive" role="alert">{error}</p>
      )}
    </section>
  );
}
